import { useState, useEffect, useCallback } from 'react'
import { api } from '../services/api'

export interface BackupSnapshot {
  readonly id: string
  readonly environmentId: string
  readonly tier: string
  readonly snapshotType: string
  readonly s3Key: string
  readonly sizeBytes: number
  readonly checksumSha256: string
  readonly encryptedAt: string
  readonly uploadedAt: string
  readonly retentionClass: string
  readonly expiresAt: string
  readonly purgedAt: string | null
}

export interface RestoreJob {
  readonly id: string
  readonly environmentId: string
  readonly snapshotId: string
  readonly targetEnvironmentId: string | null
  readonly status: string
  readonly initiatedBy: string
  readonly initiatedAt: string
  readonly completedAt: string | null
  readonly errorMessage: string | null
}

export function useBackupSnapshots(envId: string | null) {
  const [data, setData] = useState<readonly BackupSnapshot[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!envId) return
    setLoading(true)
    setError(null)
    try {
      const result = await api.get<readonly BackupSnapshot[]>(`/admin/backups/${envId}`)
      setData(result.data ?? [])
      setTotal(result.meta?.total ?? 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load snapshots')
    } finally {
      setLoading(false)
    }
  }, [envId])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { data, total, loading, error, refresh: fetch }
}

export function useInitiateRestore() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const initiate = useCallback(
    async (snapshotId: string, opts?: { targetEnvironmentId?: string; passphrase?: string }) => {
      setLoading(true)
      setError(null)
      try {
        const result = await api.post<RestoreJob>(`/admin/backups/${snapshotId}/restore`, opts ?? {})
        return result.data
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to initiate restore'
        setError(msg)
        throw err
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return { initiate, loading, error }
}

export function useRestoreJob(jobId: string | null) {
  const [data, setData] = useState<RestoreJob | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!jobId) return
    let cancelled = false
    const poll = async () => {
      setLoading(true)
      try {
        const result = await api.get<RestoreJob>(`/admin/backups/restore-jobs/${jobId}`)
        if (!cancelled) setData(result.data ?? null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    poll()
    const interval = setInterval(poll, 3000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [jobId])

  return { data, loading }
}
